import { FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChannelName, IApiConnectedAccount } from '../../types';
import { T_KEYS } from '../../constants';
import {
  AccountInfoBarContainer,
  AccountInfoLeft,
  AccountName,
  ConnectionStatusBadge,
  LastSyncText,
  AccountInfoRight,
  ActionButton,
  SettingsButton,
  ReconnectButton,
  DisconnectedAlertContainer,
  DisconnectedAlertText,
  DisconnectedAlertIcon,
} from '../../styles/ManageChannelStyles';

// Icons
const ImportIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
  </svg>
);

const RefreshIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M23 4v6h-6M1 20v-6h6" />
    <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
  </svg>
);

const SettingsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" />
  </svg>
);

const ReconnectIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M6.66667 2.66667H4.66667C3.56667 2.66667 2.66667 3.56667 2.66667 4.66667V11.3333C2.66667 12.4333 3.56667 13.3333 4.66667 13.3333H11.3333C12.4333 13.3333 13.3333 12.4333 13.3333 11.3333V9.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 2L14 4L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M8 8L14 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const AlertIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M10 6.66667V10M10 13.3333H10.0083M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C14.1421 2.5 17.5 5.85786 17.5 10Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

interface IAccountInfoBarProps {
  account: IApiConnectedAccount;
  channelName: ChannelName;
  onImportNewUnits: () => void;
  onRefreshSync: () => void | Promise<void>;
  onOpenSettings: () => void;
  onReconnect: () => void | Promise<void>;
  isRefreshing?: boolean;
  isReconnecting?: boolean;
  notImportedCount?: number;
}

const AccountInfoBar: FC<IAccountInfoBarProps> = memo(({
  account,
  channelName,
  onImportNewUnits,
  onRefreshSync,
  onOpenSettings,
  onReconnect,
  isRefreshing = false,
  isReconnecting = false,
  notImportedCount,
}) => {
  const { t } = useTranslation();

  // Calculate last sync time
  const getLastSyncText = () => {
    if (!account.updateAt) return '';
    
    const updateDate = new Date(account.updateAt);
    const now = new Date();
    const diffMs = now.getTime() - updateDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) {
      return t(T_KEYS.LAST_SYNC_NOW);
    } else if (diffMins < 60) {
      return t(T_KEYS.LAST_SYNC_MINUTES, { minutes: diffMins });
    } else {
      const diffHours = Math.floor(diffMins / 60);
      return t(T_KEYS.LAST_SYNC_HOURS, { hours: diffHours });
    }
  };

  return (
    <AccountInfoBarContainer>
      <AccountName>{account.channelName}</AccountName>
      <div className='flex justify-between'>
        <AccountInfoLeft>
          <ConnectionStatusBadge $isConnected={account.isChannelActive}>
            <span style={{ 
              width: 6, 
              height: 6, 
              borderRadius: '50%', 
              background: account.isChannelActive ? '#12b76a' : '#f04438' 
            }} />
            {account.isChannelActive ? t(T_KEYS.CONNECTED) : t(T_KEYS.DISCONNECTED)}
          </ConnectionStatusBadge>
          <LastSyncText> • {getLastSyncText()}</LastSyncText>
        </AccountInfoLeft>

        <AccountInfoRight>
            {!account.isChannelActive && (
              <ReconnectButton onClick={onReconnect} disabled={isReconnecting}>
                {isReconnecting ? t(T_KEYS.RECONNECTING) || 'Reconnecting...' : t(T_KEYS.RECONNECT_ACCOUNT)}
                {!isReconnecting && <ReconnectIcon />}
              </ReconnectButton>
            )}
              <ActionButton disabled={account.isChannelActive === false} onClick={onImportNewUnits}>
                <ImportIcon />
                {notImportedCount !== undefined && notImportedCount > 0
                  ? t(T_KEYS.IMPORT_NEW_UNITS_WITH_COUNT, { count: notImportedCount })
                  : t(T_KEYS.IMPORT_NEW_UNITS)}
              </ActionButton>
              <ActionButton disabled={account.isChannelActive === false} onClick={onRefreshSync}>
                <RefreshIcon />
                {t(T_KEYS.REFRESH_SYNC)}
              </ActionButton>
            <SettingsButton disabled={account.isChannelActive === false}  aria-label={t(T_KEYS.SETTINGS)} onClick={onOpenSettings}>
              <SettingsIcon />
            </SettingsButton>
        </AccountInfoRight>

      </div>
    </AccountInfoBarContainer>
  );
});

AccountInfoBar.displayName = 'AccountInfoBar';

export default AccountInfoBar;
